/** biome-ignore-all lint/a11y/useSemanticElements: Theme toggles use button-based radio controls for animated segmented UI while preserving radiogroup semantics. */
'use client';

import { Switch as SwitchPrimitive } from '@base-ui/react/switch';
import { Check, Moon, Sun, X } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  type FieldProps,
  FieldTooltip,
} from '@/components/atoms/forms/_shared';
import { useThemeOptional } from '@/components/ThemeProvider';
import {
  Field,
  FieldBottom,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/form-field';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

/** Color variants for the standard switch */
type ColorVariant =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'info'
  | 'success'
  | 'destructive';

/** Theme variants that render theme toggle UIs */
type ThemeVariant =
  | 'theme-gradient'
  | 'theme-switch'
  | 'theme-pill'
  | 'theme-segmented';

/** All switch variants */
export type SwitchVariant = ColorVariant | ThemeVariant;

// ============================================
// Switch (with controlled/uncontrolled support)
// ============================================

interface SwitchControlProps
  extends Omit<SwitchPrimitive.Root.Props, 'onCheckedChange'> {
  // Variants
  size?: 'sm' | 'md' | 'lg';
  variant?: SwitchVariant;
  /** Visual style of the switch (solid = filled track, outline = bordered track). Only applies to color variants. */
  switchStyle?: 'solid' | 'outline';

  // Thumb icons (color variants only)
  withIcon?: boolean;
  thumbIconOn?: React.ReactNode;
  thumbIconOff?: React.ReactNode;

  // Track labels (ON/OFF inside track, color variants only)
  showOnOff?: boolean;
  onLabel?: string;
  offLabel?: string;

  // External label
  label?: React.ReactNode;
  description?: string;
  labelPosition?: 'left' | 'right';

  /** Layout orientation for label (theme variants only) */
  orientation?: 'horizontal' | 'vertical';

  // Core (keep existing)
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export interface SwitchProps
  extends Omit<SwitchControlProps, 'label' | 'description'>,
    Omit<FieldProps, 'label' | 'description' | 'topRight' | 'bottomRight'> {
  /** Label text or ReactNode (inline with switch, or in FieldLabel when field props are set) */
  label?: React.ReactNode;
  /** Description text (inline below label, or in FieldDescription when field props are set) */
  description?: React.ReactNode;
}

// ============================================
// Color Switch Constants
// ============================================

// Icon size classes for thumb icons
const iconSizeClasses = {
  sm: 'size-2', // 8px
  md: 'size-2.5', // 10px
  lg: 'size-3', // 12px
};

// Size classes for the track
const sizeClasses = {
  sm: 'h-[18px] w-[32px]',
  md: 'h-5 w-[40px]',
  lg: 'h-6 w-[48px]',
};

// Size classes for the thumb
const thumbSizeClasses = {
  sm: 'size-4',
  md: 'size-[18px]',
  lg: 'size-[22px]',
};

// Thumb translation when checked (track width - thumb size - 2px padding on each side)
const thumbTranslateClasses = {
  sm: 'data-checked:ltr:translate-x-[14px] data-checked:rtl:-translate-x-[14px]',
  md: 'data-checked:ltr:translate-x-[20px] data-checked:rtl:-translate-x-[20px]',
  lg: 'data-checked:ltr:translate-x-[24px] data-checked:rtl:-translate-x-[24px]',
};

// Wider track sizes when ON/OFF labels are shown (extra space for text)
const sizeWithLabelClasses = {
  sm: 'h-[18px] w-[40px]',
  md: 'h-[22px] w-[48px]',
  lg: 'h-[26px] w-[56px]',
};

// Thumb translation for wider tracks with labels
const thumbTranslateWithLabelClasses = {
  sm: 'data-checked:ltr:translate-x-[22px] data-checked:rtl:-translate-x-[22px]',
  md: 'data-checked:ltr:translate-x-[26px] data-checked:rtl:-translate-x-[26px]',
  lg: 'data-checked:ltr:translate-x-[30px] data-checked:rtl:-translate-x-[30px]',
};

// Font sizes for track labels (ON/OFF)
const labelFontSizes = {
  sm: 'text-[8px]',
  md: 'text-[10px]',
  lg: 'text-xs', // 12px
};

// Solid style: colored track when checked
const solidVariantClasses: Record<ColorVariant, string> = {
  primary: 'data-checked:bg-primary',
  secondary: 'data-checked:bg-secondary',
  warning: 'data-checked:bg-warning',
  info: 'data-checked:bg-info',
  success: 'data-checked:bg-success',
  destructive: 'data-checked:bg-destructive',
};

// Outline style: colored border when checked
const outlineVariantClasses: Record<ColorVariant, string> = {
  primary: 'data-checked:border-primary',
  secondary: 'data-checked:border-secondary',
  warning: 'data-checked:border-warning',
  info: 'data-checked:border-info',
  success: 'data-checked:border-success',
  destructive: 'data-checked:border-destructive',
};

// Outline style: colored thumb when checked
const outlineThumbVariantClasses: Record<ColorVariant, string> = {
  primary: 'group-data-checked/switch:bg-primary',
  secondary: 'group-data-checked/switch:bg-secondary',
  warning: 'group-data-checked/switch:bg-warning',
  info: 'group-data-checked/switch:bg-info',
  success: 'group-data-checked/switch:bg-success',
  destructive: 'group-data-checked/switch:bg-destructive',
};

// ============================================
// Theme Variant Constants & Config
// ============================================

const themeSizeConfig = {
  sm: {
    gradient: {
      width: 'w-[64px]',
      height: 'h-6',
      indicator: 'size-5',
      travel: 'translate-x-[40px] rtl:-translate-x-[40px]',
      icon: 'size-3',
      text: 'text-xs',
      padding: 'p-0.5',
      skeletonWidth: 'w-[64px]',
      skeletonHeight: 'h-6',
    },
    switch: {
      track: 'w-[30px] h-4',
      indicator: 'size-3.5',
      travel: 'translate-x-[12px] rtl:-translate-x-[12px]',
      icon: 'size-3.5',
      gap: 'gap-1.5',
      skeletonWidth: 'w-[50px]',
      skeletonHeight: 'h-4',
    },
    pill: {
      width: 'w-[42px]',
      height: 'h-6',
      indicator: 'size-5',
      travel: 'translate-x-[18px] rtl:-translate-x-[18px]',
      icon: 'size-3',
      padding: 'p-0.5',
      skeletonWidth: 'w-[42px]',
      skeletonHeight: 'h-6',
    },
    segmented: {
      height: 'h-6',
      segmentHeight: 'h-4',
      icon: 'size-3',
      text: 'text-xs',
      padding: 'p-1',
      gap: 'gap-0.5',
    },
  },
  md: {
    gradient: {
      width: 'w-[76px]',
      height: 'h-7',
      indicator: 'size-6',
      travel: 'translate-x-[48px] rtl:-translate-x-[48px]',
      icon: 'size-3.5',
      text: 'text-sm',
      padding: 'p-0.5',
      skeletonWidth: 'w-[76px]',
      skeletonHeight: 'h-7',
    },
    switch: {
      track: 'w-9 h-[18px]',
      indicator: 'size-4',
      travel: 'translate-x-[16px] rtl:-translate-x-[16px]',
      icon: 'size-4',
      gap: 'gap-2',
      skeletonWidth: 'w-[60px]',
      skeletonHeight: 'h-[18px]',
    },
    pill: {
      width: 'w-[50px]',
      height: 'h-7',
      indicator: 'size-6',
      travel: 'translate-x-[22px] rtl:-translate-x-[22px]',
      icon: 'size-3.5',
      padding: 'p-0.5',
      skeletonWidth: 'w-[50px]',
      skeletonHeight: 'h-7',
    },
    segmented: {
      height: 'h-7',
      segmentHeight: 'h-5',
      icon: 'size-3.5',
      text: 'text-sm',
      padding: 'p-1',
      gap: 'gap-1',
    },
  },
  lg: {
    gradient: {
      width: 'w-[88px]',
      height: 'h-8',
      indicator: 'size-7',
      travel: 'translate-x-[56px] rtl:-translate-x-[56px]',
      icon: 'size-4',
      text: 'text-sm',
      padding: 'p-0.5',
      skeletonWidth: 'w-[88px]',
      skeletonHeight: 'h-8',
    },
    switch: {
      track: 'w-[42px] h-5',
      indicator: 'size-[18px]',
      travel: 'translate-x-[20px] rtl:-translate-x-[20px]',
      icon: 'size-[18px]',
      gap: 'gap-2.5',
      skeletonWidth: 'w-[70px]',
      skeletonHeight: 'h-5',
    },
    pill: {
      width: 'w-[58px]',
      height: 'h-8',
      indicator: 'size-7',
      travel: 'translate-x-[26px] rtl:-translate-x-[26px]',
      icon: 'size-4',
      padding: 'p-0.5',
      skeletonWidth: 'w-[58px]',
      skeletonHeight: 'h-8',
    },
    segmented: {
      height: 'h-8',
      segmentHeight: 'h-6',
      icon: 'size-4',
      text: 'text-sm',
      padding: 'p-1',
      gap: 'gap-1',
    },
  },
} as const;

// Gradient directions matching Figma design
const GRADIENT_LIGHT =
  'linear-gradient(247deg, var(--destructive) 10%, var(--orange-500) 50%, var(--warning) 90%)';
const GRADIENT_DARK =
  'linear-gradient(264deg, var(--blue-200) 3%, var(--indigo-400) 60%, var(--blue-800) 98%)';

// Shared theme style constants
const themeFocusRingClasses =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const themeTransitionClasses =
  'duration-200 ease-smooth motion-reduce:transition-none';

// ============================================
// Theme Variant Renderers
// ============================================

function GradientToggle({
  isLight,
  onThemeChange,
  sizes,
}: {
  isLight: boolean;
  onThemeChange: (theme: 'light' | 'dark') => void;
  sizes: (typeof themeSizeConfig)['md']['gradient'];
}) {
  return (
    <div
      data-slot="switch"
      className={cn(
        'relative flex items-center rounded-full overflow-hidden',
        sizes.width,
        sizes.height,
        sizes.padding,
      )}
      style={{
        background: isLight ? GRADIENT_LIGHT : GRADIENT_DARK,
      }}
      role="radiogroup"
      aria-label="Theme selector"
    >
      {/* Sliding indicator */}
      <div
        data-slot="switch-thumb"
        className={cn(
          'absolute start-0.5 flex items-center justify-center rounded-full bg-card shadow-md hover:shadow-lg transition-[translate,box-shadow] z-10',
          sizes.indicator,
          themeTransitionClasses,
          !isLight && sizes.travel,
        )}
      >
        {isLight ? (
          <Sun className={cn(sizes.icon, 'text-warning')} />
        ) : (
          <Moon className={cn(sizes.icon, 'text-info')} />
        )}
      </div>

      {/* Clickable areas */}
      <button
        type="button"
        role="radio"
        aria-checked={isLight}
        aria-label="Light theme"
        onClick={() => onThemeChange('light')}
        className={cn('flex-1 h-full rounded-full z-20', themeFocusRingClasses)}
      />
      <button
        type="button"
        role="radio"
        aria-checked={!isLight}
        aria-label="Dark theme"
        onClick={() => onThemeChange('dark')}
        className={cn('flex-1 h-full rounded-full z-20', themeFocusRingClasses)}
      />

      {/* Label text */}
      <span
        className={cn(
          'absolute pointer-events-none font-medium text-white transition-opacity',
          sizes.text,
          themeTransitionClasses,
          isLight ? 'end-2 opacity-100' : 'end-2 opacity-0',
        )}
      >
        Light
      </span>
      <span
        className={cn(
          'absolute pointer-events-none font-medium text-white transition-opacity',
          sizes.text,
          themeTransitionClasses,
          !isLight ? 'start-2 opacity-100' : 'start-2 opacity-0',
        )}
      >
        Dark
      </span>
    </div>
  );
}

function ThemeSwitchToggle({
  isLight,
  onThemeChange,
  sizes,
}: {
  isLight: boolean;
  onThemeChange: (theme: 'light' | 'dark') => void;
  sizes: (typeof themeSizeConfig)['md']['switch'];
}) {
  return (
    <div
      className={cn('flex items-center', sizes.gap)}
      role="radiogroup"
      aria-label="Theme selector"
      data-slot="switch"
    >
      <button
        type="button"
        role="radio"
        aria-checked={isLight}
        aria-label={`Switch to ${isLight ? 'dark' : 'light'} theme`}
        onClick={() => onThemeChange(isLight ? 'dark' : 'light')}
        className={cn(
          'relative rounded-full transition-[background-color,box-shadow] hover:shadow-[0_0_0_3px_var(--ring)/0.1]',
          sizes.track,
          themeTransitionClasses,
          themeFocusRingClasses,
          isLight ? 'bg-input' : 'bg-primary',
        )}
      >
        <div
          data-slot="switch-thumb"
          className={cn(
            'absolute start-0.5 top-1/2 -translate-y-1/2 rounded-full shadow-md transition-[translate,background-color,box-shadow]',
            sizes.indicator,
            themeTransitionClasses,
            isLight ? 'bg-card' : 'bg-background',
            !isLight && sizes.travel,
          )}
        />
      </button>
      <div className={cn(sizes.icon, 'text-[var(--muted-foreground)]')}>
        {isLight ? (
          <Moon className="size-full" />
        ) : (
          <Sun className="size-full" />
        )}
      </div>
    </div>
  );
}

function PillToggle({
  isLight,
  onThemeChange,
  sizes,
}: {
  isLight: boolean;
  onThemeChange: (theme: 'light' | 'dark') => void;
  sizes: (typeof themeSizeConfig)['md']['pill'];
}) {
  return (
    <div
      className={cn(
        'relative flex items-center justify-between rounded-full bg-input',
        sizes.width,
        sizes.height,
        sizes.padding,
      )}
      role="radiogroup"
      aria-label="Theme selector"
      data-slot="switch"
    >
      {/* Moon icon (left) */}
      <button
        type="button"
        role="radio"
        aria-checked={!isLight}
        aria-label="Dark theme"
        tabIndex={!isLight ? -1 : 0}
        aria-hidden={!isLight ? true : undefined}
        onClick={() => onThemeChange('dark')}
        className={cn(
          'z-10 flex items-center justify-center rounded-full transition-opacity',
          sizes.indicator,
          themeTransitionClasses,
          themeFocusRingClasses,
          isLight ? 'opacity-50' : 'opacity-0',
        )}
      >
        <Moon className={cn(sizes.icon, 'text-[var(--foreground)]')} />
      </button>

      {/* Sun icon (right) */}
      <button
        type="button"
        role="radio"
        aria-checked={isLight}
        aria-label="Light theme"
        tabIndex={isLight ? -1 : 0}
        aria-hidden={isLight ? true : undefined}
        onClick={() => onThemeChange('light')}
        className={cn(
          'z-10 flex items-center justify-center rounded-full transition-opacity',
          sizes.indicator,
          themeTransitionClasses,
          themeFocusRingClasses,
          !isLight ? 'opacity-50' : 'opacity-0',
        )}
      >
        <Sun className={cn(sizes.icon, 'text-[var(--foreground)]')} />
      </button>

      {/* Sliding indicator with icon */}
      <div
        data-slot="switch-thumb"
        className={cn(
          'absolute start-0.5 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full bg-card shadow-md hover:shadow-lg transition-[translate,box-shadow]',
          sizes.indicator,
          themeTransitionClasses,
          isLight && sizes.travel,
        )}
      >
        {isLight ? (
          <Sun className={cn(sizes.icon, 'text-warning')} />
        ) : (
          <Moon className={cn(sizes.icon, 'text-info')} />
        )}
      </div>
    </div>
  );
}

function SegmentedToggle({
  isLight,
  onThemeChange,
  sizes,
  placeholder = false,
}: {
  isLight: boolean;
  onThemeChange: (theme: 'light' | 'dark') => void;
  sizes: (typeof themeSizeConfig)['md']['segmented'];
  placeholder?: boolean;
}) {
  // Hidden spans retain the labels' intrinsic width and the inline-flex baseline.
  const Segment = placeholder ? 'span' : 'button';
  return (
    <div
      className={cn(
        'inline-flex items-center rounded bg-input',
        sizes.height,
        sizes.padding,
        sizes.gap,
        placeholder && 'rounded-full bg-[var(--surface-1)]',
      )}
      {...(!placeholder && {
        role: 'radiogroup',
        'aria-label': 'Theme selector',
      })}
      data-slot={placeholder ? 'switch-theme-placeholder' : 'switch'}
    >
      {/* Light segment */}
      <Segment
        type={placeholder ? undefined : 'button'}
        role={placeholder ? undefined : 'radio'}
        aria-checked={placeholder ? undefined : isLight}
        aria-label={placeholder ? undefined : 'Light theme'}
        onClick={placeholder ? undefined : () => onThemeChange('light')}
        className={cn(
          'flex items-center justify-center gap-1 rounded px-2 whitespace-nowrap',
          sizes.segmentHeight,
          sizes.text,
          placeholder
            ? 'invisible'
            : cn(
                'transition-[background-color,color,box-shadow,opacity]',
                themeTransitionClasses,
                themeFocusRingClasses,
              ),
          !placeholder &&
            (isLight
              ? 'bg-card text-foreground shadow-sm'
              : 'text-[var(--foreground)] opacity-50 hover:opacity-70'),
        )}
      >
        <Sun className={sizes.icon} />
        <span>Light</span>
      </Segment>

      {/* Dark segment */}
      <Segment
        type={placeholder ? undefined : 'button'}
        role={placeholder ? undefined : 'radio'}
        aria-checked={placeholder ? undefined : !isLight}
        aria-label={placeholder ? undefined : 'Dark theme'}
        onClick={placeholder ? undefined : () => onThemeChange('dark')}
        className={cn(
          'flex items-center justify-center gap-1 rounded px-2 whitespace-nowrap',
          sizes.segmentHeight,
          sizes.text,
          placeholder
            ? 'invisible'
            : cn(
                'transition-[background-color,color,box-shadow,opacity]',
                themeTransitionClasses,
                themeFocusRingClasses,
              ),
          !placeholder &&
            (!isLight
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm'
              : 'text-[var(--foreground)] opacity-50 hover:opacity-70'),
        )}
      >
        <Moon className={sizes.icon} />
        <span>Dark</span>
      </Segment>
    </div>
  );
}

// ============================================
// Theme Variant Renderer (auto-connects to ThemeProvider)
// ============================================

const themeVariantKeyMap: Record<
  ThemeVariant,
  'gradient' | 'switch' | 'pill' | 'segmented'
> = {
  'theme-gradient': 'gradient',
  'theme-switch': 'switch',
  'theme-pill': 'pill',
  'theme-segmented': 'segmented',
};

const ThemeVariantRenderer = React.forwardRef<
  HTMLDivElement,
  SwitchControlProps
>(
  (
    {
      variant,
      size = 'md',
      label,
      orientation = 'vertical',
      checked: controlledChecked,
      onCheckedChange,
      className: classNameProp,
      description: _description,
      labelPosition: _labelPosition,
      switchStyle: _switchStyle,
      withIcon: _withIcon,
      thumbIconOn: _thumbIconOn,
      thumbIconOff: _thumbIconOff,
      showOnOff: _showOnOff,
      onLabel: _onLabel,
      offLabel: _offLabel,
      defaultChecked: _defaultChecked,
      ...restProps
    },
    ref,
  ) => {
    // className from base-ui can be string | function; theme variants only use string
    const className =
      typeof classNameProp === 'function' ? undefined : classNameProp;
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    const themeContext = useThemeOptional();
    const contextTheme = themeContext?.theme;
    const contextSetTheme = themeContext?.setTheme;

    // Auto-connect to ThemeProvider when no explicit checked/onCheckedChange
    const isControlled = controlledChecked !== undefined;
    const activeTheme: 'light' | 'dark' = isControlled
      ? controlledChecked
        ? 'dark'
        : 'light'
      : ((contextTheme ?? 'light') as 'light' | 'dark');

    const handleThemeChange = React.useCallback(
      (newTheme: 'light' | 'dark') => {
        const newChecked = newTheme === 'dark';
        if (isControlled) {
          onCheckedChange?.(newChecked);
        } else if (contextSetTheme) {
          contextSetTheme(newTheme);
        }
      },
      [isControlled, onCheckedChange, contextSetTheme],
    );

    const key = themeVariantKeyMap[variant as ThemeVariant];

    const sizes = themeSizeConfig[size][key];

    const isLight = activeTheme === 'light';

    // SSR placeholder: same wrapper + same display as the mounted toggle so
    // wrapper bounds do not change on hydration.
    const isPlaceholder = !mounted && !isControlled;

    let toggle: React.ReactNode;
    switch (isPlaceholder && key !== 'segmented' ? null : key) {
      case 'gradient':
        toggle = (
          <GradientToggle
            isLight={isLight}
            onThemeChange={handleThemeChange}
            sizes={sizes as (typeof themeSizeConfig)['md']['gradient']}
          />
        );
        break;
      case 'switch':
        toggle = (
          <ThemeSwitchToggle
            isLight={isLight}
            onThemeChange={handleThemeChange}
            sizes={sizes as (typeof themeSizeConfig)['md']['switch']}
          />
        );
        break;
      case 'pill':
        toggle = (
          <PillToggle
            isLight={isLight}
            onThemeChange={handleThemeChange}
            sizes={sizes as (typeof themeSizeConfig)['md']['pill']}
          />
        );
        break;
      case 'segmented':
        toggle = (
          <SegmentedToggle
            isLight={isLight}
            onThemeChange={handleThemeChange}
            sizes={sizes as (typeof themeSizeConfig)['md']['segmented']}
            placeholder={isPlaceholder}
          />
        );
        break;
      default:
        toggle = (
          <div
            data-slot="switch-theme-placeholder"
            className={cn(
              'flex rounded-full bg-[var(--surface-1)]',
              'skeletonWidth' in sizes && sizes.skeletonWidth,
              'skeletonHeight' in sizes && sizes.skeletonHeight,
            )}
          />
        );
    }

    // Pick standard HTML attributes safe for div (aria-*, data-*, id).
    // Pre-mount placeholder is a hidden, inert skeleton: forward only
    // identity/geometry attrs (id, style, data-*); drop role/tabIndex/aria-*/handlers.
    const allProps = restProps as React.HTMLAttributes<HTMLDivElement>;
    const divProps: React.HTMLAttributes<HTMLDivElement> = isPlaceholder
      ? {
          ...Object.fromEntries(
            Object.entries(allProps).filter(
              ([k]) => k === 'id' || k === 'style' || k.startsWith('data-'),
            ),
          ),
          'aria-hidden': true,
          inert: true,
        }
      : allProps;

    if (!label) {
      return (
        <div ref={ref} className={className} {...divProps}>
          {toggle}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex',
          orientation === 'vertical'
            ? 'flex-col items-start gap-2'
            : 'flex-row items-center gap-3',
          className,
        )}
        {...divProps}
      >
        <span className="text-xs font-semibold text-[var(--muted-foreground)]">
          {label}
        </span>
        {toggle}
      </div>
    );
  },
);

ThemeVariantRenderer.displayName = 'ThemeVariantRenderer';

// ============================================
// Main Switch Component
// ============================================

/** Type guard: is the variant a color variant? */
function isColorVariant(v: SwitchVariant): v is ColorVariant {
  return !v.startsWith('theme-');
}

const SwitchControl = React.forwardRef<HTMLButtonElement, SwitchControlProps>(
  (
    {
      label,
      description,
      labelPosition = 'right',
      orientation,
      className,
      id,
      size = 'md',
      variant = 'primary',
      switchStyle = 'solid',
      withIcon,
      thumbIconOn,
      thumbIconOff,
      showOnOff,
      onLabel,
      offLabel,
      checked: controlledChecked,
      defaultChecked = false,
      onCheckedChange,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const switchId = id || generatedId;

    const [internalChecked, setInternalChecked] =
      React.useState(defaultChecked);
    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : internalChecked;

    const handleCheckedChange = React.useCallback(
      (newChecked: boolean) => {
        if (!isControlled) {
          setInternalChecked(newChecked);
        }
        onCheckedChange?.(newChecked);
      },
      [isControlled, onCheckedChange],
    );

    // Dispatch to theme variant renderer if applicable
    if (!isColorVariant(variant)) {
      return (
        <ThemeVariantRenderer
          ref={ref as React.Ref<HTMLDivElement>}
          variant={variant}
          size={size}
          label={label}
          orientation={orientation}
          checked={controlledChecked}
          onCheckedChange={onCheckedChange}
          className={className}
          {...props}
        />
      );
    }

    // --- Color variant rendering (unchanged) ---
    const colorVariant: ColorVariant = variant;

    // Use wider track when labels are present to prevent text cutoff
    const hasLabels = showOnOff || !!onLabel || !!offLabel;
    const activeSizeClasses = hasLabels ? sizeWithLabelClasses : sizeClasses;
    const activeThumbTranslate = hasLabels
      ? thumbTranslateWithLabelClasses
      : thumbTranslateClasses;

    // Build track classes based on style
    const trackClasses =
      switchStyle === 'solid'
        ? cn(
            'data-unchecked:bg-muted-foreground border-transparent',
            'shadow-xs',
            solidVariantClasses[colorVariant],
          )
        : cn(
            'data-unchecked:bg-transparent data-checked:bg-transparent border-input',
            outlineVariantClasses[colorVariant],
          );

    // Build thumb classes based on style
    const thumbClasses =
      switchStyle === 'solid'
        ? 'bg-background dark:data-unchecked:bg-foreground dark:data-checked:bg-background'
        : cn(
            'bg-input dark:bg-foreground',
            outlineThumbVariantClasses[colorVariant],
          );

    // Thumb icon logic
    const showThumbIcon = withIcon || thumbIconOn || thumbIconOff;
    const checkedIcon = showThumbIcon
      ? thumbIconOn || <Check className={iconSizeClasses[size]} />
      : null;
    const uncheckedIcon = showThumbIcon
      ? thumbIconOff || <X className={iconSizeClasses[size]} />
      : null;

    const switchElement = (
      <SwitchPrimitive.Root
        ref={ref}
        id={switchId}
        data-slot="switch"
        data-size={size}
        data-variant={colorVariant}
        data-style={switchStyle}
        className={cn(
          // Base styles
          'peer group/switch relative inline-flex items-center shrink-0 rounded-full border',
          'transition-[background-color,border-color,box-shadow] duration-200 ease-smooth motion-reduce:transition-none outline-none',
          'after:absolute after:-inset-x-3 after:-inset-y-[13px]',
          'data-disabled:cursor-not-allowed data-disabled:opacity-50',
          // Hover: subtle track glow
          'not-data-disabled:hover:shadow-[0_0_0_3px_var(--ring)/0.08]',
          // Focus styles
          'focus-visible:border-ring focus-visible:ring-gray-400/50 focus-visible:ring-[3px]',
          // Invalid styles
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 aria-invalid:ring-[3px]',
          // Size classes (wider when labels are shown)
          activeSizeClasses[size],
          // Variant/style classes
          trackClasses,
          className,
        )}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        {...props}
      >
        {hasLabels && (
          <>
            <span
              aria-hidden="true"
              className={cn(
                'absolute font-semibold transition-opacity duration-200 ease-smooth motion-reduce:transition-none',
                labelFontSizes[size],
                'start-1.5',
                checked ? 'opacity-100' : 'opacity-0',
                switchStyle === 'solid'
                  ? 'text-primary-foreground'
                  : 'text-current',
              )}
            >
              {onLabel || 'ON'}
            </span>
            <span
              aria-hidden="true"
              className={cn(
                'absolute font-semibold transition-opacity duration-200 ease-smooth motion-reduce:transition-none',
                labelFontSizes[size],
                'end-1.5',
                checked ? 'opacity-0' : 'opacity-100',
                switchStyle === 'solid'
                  ? 'text-foreground dark:text-primary-foreground'
                  : 'text-current',
              )}
            >
              {offLabel || 'OFF'}
            </span>
          </>
        )}
        <SwitchPrimitive.Thumb
          data-slot="switch-thumb"
          className={cn(
            'pointer-events-none block rounded-full ring-0',
            'shadow-sm group-hover/switch:shadow-md group-active/switch:shadow-sm',
            'transition-[translate,box-shadow,background-color] duration-200 motion-reduce:transition-none',
            'ease-smooth',
            'data-unchecked:translate-x-0',
            // Size classes
            thumbSizeClasses[size],
            // Translation classes (wider when labels are shown)
            activeThumbTranslate[size],
            // Color classes based on style
            thumbClasses,
            // Flex for centering icons
            showThumbIcon && 'flex items-center justify-center',
          )}
        >
          {showThumbIcon && (
            <span
              aria-hidden="true"
              className={cn(
                'flex items-center justify-center transition-colors duration-200 ease-smooth motion-reduce:transition-none',
                // Icon colors based on style
                switchStyle === 'solid'
                  ? 'text-muted-foreground group-data-checked/switch:text-primary'
                  : 'text-background group-data-checked/switch:text-primary-foreground',
              )}
            >
              {checked ? checkedIcon : uncheckedIcon}
            </span>
          )}
        </SwitchPrimitive.Thumb>
      </SwitchPrimitive.Root>
    );

    if (!label) {
      return switchElement;
    }

    return (
      <div
        className={cn(
          'flex items-center gap-2',
          labelPosition === 'left' && 'flex-row-reverse justify-end',
        )}
      >
        {switchElement}
        <div className="flex flex-col gap-0.5">
          <label
            htmlFor={switchId}
            className="text-sm font-medium leading-5 cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </label>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
    );
  },
);

SwitchControl.displayName = 'SwitchControl';

// =============================================================================
// Switch — public API with smart field mode (inline layout)
// =============================================================================

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (props, ref) => {
    // Extract field-specific props (error, tooltip, required, labelIcon, fieldClassName)
    // label and description are shared — we handle them manually
    const {
      label,
      description,
      error,
      tooltip,
      required,
      labelIcon,
      fieldClassName,
      ...controlProps
    } = props;

    const isFieldMode = !!(error || tooltip || required || labelIcon);

    if (!isFieldMode) {
      // Bare mode: pass label/description to control for inline rendering
      return (
        <SwitchControl
          ref={ref}
          label={label}
          description={description as string}
          {...controlProps}
        />
      );
    }

    // Field mode: label goes to FieldLabel, switch renders bare
    return (
      <Field
        layout="inline"
        state={error ? 'destructive' : undefined}
        disabled={controlProps.disabled}
        className={fieldClassName}
      >
        <SwitchControl ref={ref} {...controlProps} />
        {label && (
          <FieldLabel required={required} labelIcon={labelIcon}>
            {label}
            {tooltip && <FieldTooltip content={tooltip} />}
          </FieldLabel>
        )}
        {(error || description) && (
          <FieldBottom>
            {error && <FieldError>{error}</FieldError>}
            {!error && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </FieldBottom>
        )}
      </Field>
    );
  },
);
Switch.displayName = 'Switch';

export { Switch };
